import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CampusUserGroup } from '@chancenwerk/db/CampusUserGroup.js';
import { CampusNotificationFilter } from '@chancenwerk/db/filter/CampusNotificationFilter.js';
import { CampusNotification } from '@chancenwerk/db/notifications/CampusNotification.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { combineLatest, filter, switchMap } from 'rxjs';

const studentSelector: BaseQLSelectionSet<Student> = {
  _id: 1,
  _class: 1,
  schoolInformation: {
    _class: 1,
    jahrgangsstufe: 1,
  },
  kpfs: {},
};

/**
 * @customElement campus-page-all-notifications
 */
@customElement('campus-page-all-notifications')
export class CampusPageAllNotifications extends CrPage {
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  private readonly _columnDefinitions: Array<ColumnDefinition<typeof CampusNotification>> = [
    {
      columnName: 'angelegt am',
      fieldName: 'createdAt',
      getFieldMapping(entityInstance) {
        return entityInstance.createdAt?.toFormat('dd.LL.yyyy');
      },
      columnWidth: '150px',
    },
    {
      columnName: 'Titel',
      fieldName: 'title',
      getFieldMapping(entityInstance) {
        return entityInstance.title;
      },
    },
    {
      columnName: 'Inhalt',
      fieldName: 'content',
      renderItem(entityInstance) {
        return html` <cr-text>${unsafeHTML(entityInstance.content)} </cr-text> `;
      },
    },
  ];

  @state() private readonly _groupsOfUser = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(user => CampusUserGroup.getStudentGroupsForStudentID(user?._id ?? '')({ _class: 1, _id: 1 })),
    ),
  );

  @state() private readonly _filter = new RXController(
    this,
    combineLatest([this._currentUser.observable, this._groupsOfUser.observable]).pipe(
      filter(Boolean),
      switchMap(async ([user, groups]) => {
        let schoolIDs: string[] = [];
        let grade: undefined | number;
        if (user?._class === Student._class) {
          const student = await Student.getByID<Student>(user._id)(studentSelector);
          schoolIDs = student?.kpfs?.map(kpf => kpf._id);
          grade = student?.schoolInformation.jahrgangsstufe;
        }

        return new CampusNotificationFilter({
          forEmployees: user?._class !== Student._class,
          forGrades: grade ? [grade] : ChancenwerkUser.availableGrades,
          forGroupIDs: groups?.map(g => g._id),
          forSchoolIDs: schoolIDs,
          userID: user?._id,
          noTimeLimit: true,
        });
      }),
    ),
  );

  constructor() {
    super();
    this.label = 'Alle Neuigkeiten';
    this.description = 'Hier kannst du alle deine vergangenen Neuigkeiten einsehen';
    this.permission = ChancenPermission.global.READ_NOTIFICATIONS;
    this.topScrollButton = true;
  }

  override content() {
    return html`
      <x-data-view
        .options=${{
          class: CampusNotification,
          view: new View({ filter: this._filter.value }),
          operation: CampusNotification.subscribeNotificationsByFilterPaginated,
          selectionSet: CampusNotification.allFields,
          onlyShowLastTranslation: true,
          customColumns: this._columnDefinitions,
        } as DataViewOptions<typeof CampusNotification>}
      >
      </x-data-view>
    `;
  }
}
